import type { AssistantFeedbackRating, AssistantMessageId } from "@inrev/common";
import { Button, ClickableIcon, Text } from "@portal/components/primitives";
import { type ReactNode, useState } from "react";

export const AdminAssistantFeedback = ({
	comment: initialComment,
	disabled,
	messageId,
	onSubmit,
	rating: initialRating,
	trailing,
}: {
	comment?: string | null;
	disabled?: boolean;
	messageId: AssistantMessageId;
	onSubmit: (input: {
		messageId: AssistantMessageId;
		rating: AssistantFeedbackRating;
		comment?: string;
	}) => Promise<unknown>;
	rating?: AssistantFeedbackRating;
	/** Extra actions rendered in the same row as the ratings — a copy control, typically. */
	trailing?: ReactNode;
}) => {
	const [rating, setRating] = useState<AssistantFeedbackRating | undefined>(initialRating);
	const [comment, setComment] = useState(initialComment ?? "");
	const [submittedComment, setSubmittedComment] = useState((initialComment ?? "").trim());
	const [commentSaved, setCommentSaved] = useState(submittedComment !== "");
	const [status, setStatus] = useState<"idle" | "saving" | "error">("idle");
	const saving = status === "saving";
	const trimmedComment = comment.trim();

	const submit = async (nextRating: AssistantFeedbackRating) => {
		if (disabled || saving) return;
		const previousRating = rating;
		setRating(nextRating);
		setStatus("saving");
		try {
			await onSubmit({
				messageId,
				rating: nextRating,
				comment: trimmedComment === "" ? undefined : trimmedComment,
			});
			setSubmittedComment(trimmedComment);
			setCommentSaved((saved) => saved || trimmedComment !== "");
			setStatus("idle");
		} catch {
			setRating(previousRating);
			setStatus("error");
		}
	};

	// Every submit upserts the feedback row and publishes an AssistantFeedbackSubmittedEvent.
	const commentUnchanged = trimmedComment === submittedComment;

	return (
		<div className="flex flex-col gap-1" data-testid="assistant-feedback">
			<div className="flex items-center gap-3.5">
				<ClickableIcon
					aria-label="Thumbs up"
					color={rating === "up" ? "confirm" : "tertiary"}
					data-testid="assistant-feedback-up"
					disabled={disabled || saving}
					filled={rating === "up"}
					onClick={() => void submit("up")}
					size="mini"
					type="hand-thumb-up"
				/>
				<ClickableIcon
					aria-label="Thumbs down"
					color={rating === "down" ? "danger" : "tertiary"}
					data-testid="assistant-feedback-down"
					disabled={disabled || saving}
					filled={rating === "down"}
					onClick={() => void submit("down")}
					size="mini"
					type="hand-thumb-down"
				/>
				{trailing}
			</div>
			{status === "error" && (
				<Text color="danger" data-testid="assistant-feedback-error" role="alert" type="caption">
					Couldn't save feedback. Try again.
				</Text>
			)}
			{rating !== undefined && (
				<div className="flex flex-col gap-1.5 rounded-md border border-bc-default bg-surface-base px-2 pt-1 pb-1.5 focus-within:border-bc-strong">
					<textarea
						className="min-h-10 w-full resize-none bg-transparent text-[12px] text-primary placeholder:text-tertiary focus:outline-none"
						data-testid="assistant-feedback-comment"
						disabled={disabled}
						onChange={(event) => {
							setComment(event.target.value);
							if (status === "error") setStatus("idle");
						}}
						placeholder="Optional comment"
						value={comment}
					/>
					<div className="flex items-center justify-end gap-2">
						{commentSaved && commentUnchanged && status === "idle" && (
							<Text color="confirm" data-testid="assistant-feedback-saved" type="caption">
								Saved
							</Text>
						)}
						<Button
							color="primary"
							data-testid="assistant-feedback-submit"
							disabled={disabled || commentUnchanged}
							loading={saving}
							onClick={() => void submit(rating)}
							size="sm"
						>
							Submit
						</Button>
					</div>
				</div>
			)}
		</div>
	);
};
